import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Cpu, Wallet, Scale, Megaphone, Rocket,
  BarChart3, Package, Headphones, Radio, Zap, ArrowRight,
  Check, FileText, Send, TrendingUp, Briefcase,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Agent roster (10 agents) — displayed as full portrait cards with real faces
// ---------------------------------------------------------------------------

const AGENTS = [
  {
    code: "gaby", name: "GABY", role: "Orchestrateur", icon: Sparkles, accent: "#3B82F6", tagline: "Le chef d'orchestre",
    image: "/agents/gaby-poster.png",
  },
  {
    code: "commerce", name: "JÉRÔME", role: "Directeur commercial", icon: Briefcase, accent: "#D4A017", tagline: "Multi-apps · abos & CA",
    image: "/agents/commerce-poster.png",
  },
  {
    code: "chanti", name: "THIERRY", role: "Product Manager", icon: Rocket, accent: "#F59E0B", tagline: "Roadmap & feedback",
    image: "/agents/thierry-poster.png",
  },
  {
    code: "casper", name: "CASPER", role: "Legal & Compliance", icon: Scale, accent: "#EF4444", tagline: "RGPD, CGV, contrats",
    image: "/agents/casper-poster.png",
  },
  {
    code: "clara", name: "CLARA", role: "Marketing & Growth", icon: Megaphone, accent: "#EC4899", tagline: "Content & outreach",
    image: "/agents/clara-poster.png",
  },
  {
    code: "fisco", name: "ULRICH", role: "Finance", icon: Wallet, accent: "#10B981", tagline: "MRR, factures, dunning",
    image: "/agents/ulrich-poster.png",
  },
  {
    code: "rhea", name: "RHÉA", role: "Customer Success", icon: Headphones, accent: "#8B5CF6", tagline: "Onboarding & rétention",
    image: "/agents/rhea-poster.png",
  },
  {
    code: "opera", name: "OPÉRA", role: "Ops & Support", icon: Cpu, accent: "#06B6D4", tagline: "Tickets & incidents",
    image: "/agents/opera-poster.png",
  },
  {
    code: "visio", name: "VISIO", role: "Business Analytics", icon: BarChart3, accent: "#F97316", tagline: "KPIs & insights",
    image: "/agents/visio-poster.png",
  },
  {
    code: "voxy", name: "VOXY", role: "Voice AI", icon: Radio, accent: "#A855F7", tagline: "Appels sortants",
    image: "/agents/voxy-poster.png",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#050507] text-zinc-100 overflow-hidden relative font-sans" data-testid="landing-page">
      {/* Animated grid + gradient orbs (background) */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(59,130,246,0.15),transparent_45%),radial-gradient(circle_at_80%_60%,rgba(236,72,153,0.12),transparent_45%),radial-gradient(circle_at_50%_100%,rgba(139,92,246,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 grid place-items-center bg-white text-black font-bold text-sm">GX</div>
          <div>
            <div className="font-semibold tracking-tight text-lg leading-none">GABRYX</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">AI Cockpit</div>
          </div>
        </div>
        <Link
          to="/app"
          data-testid="nav-enter-app"
          className="inline-flex items-center gap-2 bg-white text-black text-xs font-medium px-4 py-2 hover:bg-zinc-200 transition"
        >
          Entrer dans le cockpit
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </nav>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-12 pt-12 md:pt-24 pb-12 md:pb-16 max-w-7xl mx-auto">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-400 border border-emerald-500/20 bg-emerald-500/[0.05] px-3 py-1.5 mb-8">
          <Zap className="w-3 h-3" />
          Solo SaaS Command Center · 2026
        </div>
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] max-w-5xl">
          <span className="block">Confie un <span className="italic font-serif">objectif</span>.</span>
          <span className="block bg-gradient-to-r from-blue-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">Pas un prompt.</span>
          <span className="block text-zinc-500">Ton équipe IA fait le reste.</span>
        </h1>
        <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mt-8 leading-relaxed">
          GABRYX est le cockpit du fondateur solo qui refuse de faire à la main ce qu&apos;une équipe IA
          fait mieux, plus vite, sans jamais dormir. 10 agents nommés — Marketing, ventes, finance,
          légal, produit, ops — tous sous un seul écran, tous prêts à exécuter.
        </p>
        <div className="flex items-center gap-3 mt-10 flex-wrap">
          <Link
            to="/app"
            data-testid="hero-cta-primary"
            className="inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-6 py-3.5 hover:bg-zinc-200 transition"
          >
            Ouvrir mon cockpit
            <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="#agents" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition px-6 py-3.5 border border-white/10">
            Rencontrer l&apos;équipe
          </a>
        </div>
      </section>

      {/* Live activity feed — "En action" section (Delos-style mockups) */}
      <LiveActivityShowcase />

      {/* Agents grid — Limova style with idle animations */}
      <section id="agents" className="relative z-10 px-6 md:px-12 py-16 md:py-24 max-w-7xl mx-auto">
        <div className="mb-12 md:mb-16">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Ton équipe · 10 agents</div>
          <h2 className="text-3xl md:text-5xl font-light tracking-tighter max-w-3xl">
            Chacun expert de son domaine.
            <br />
            <span className="text-zinc-500">Aucun ne dort.</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {AGENTS.map((a, i) => (
            <AgentCard key={a.code} agent={a} delay={i * 60} />
          ))}
        </div>
      </section>

      {/* Concept explainer */}
      <section className="relative z-10 px-6 md:px-12 py-20 md:py-32 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-400 mb-4">Le concept</div>
            <h3 className="text-3xl md:text-4xl font-light tracking-tighter leading-tight">
              10 agents. Une seule vue. <span className="text-zinc-500">Zéro contexte perdu.</span>
            </h3>
          </div>
          <div className="space-y-6 text-[15px] text-zinc-300 leading-relaxed">
            <p>
              Chaque matin, GABY te livre un briefing synthétique : nouveaux abonnés, MRR, incidents, à faire du jour.
            </p>
            <p>
              CLARA rédige tes posts et emails. ULRICH surveille ton MRR et déclenche les relances. RHÉA détecte les users qui décrochent. CASPER veille RGPD.
            </p>
            <p className="text-white">
              Tu penses stratégie. Ils exécutent.
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio hint */}
      <section className="relative z-10 px-6 md:px-12 py-16 max-w-6xl mx-auto">
        <div className="border border-white/10 bg-white/[0.02] p-8 md:p-12">
          <div className="flex items-start gap-4 flex-wrap">
            <Package className="w-10 h-10 text-blue-400" />
            <div className="flex-1 min-w-[280px]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-blue-400 mb-2">Portefeuille</div>
              <h4 className="text-2xl md:text-3xl font-light tracking-tighter mb-3">
                Aujourd&apos;hui <span className="text-white font-medium">CHANTIX</span>.
                <br />
                <span className="text-zinc-500">Demain, tes prochaines apps.</span>
              </h4>
              <p className="text-sm text-zinc-400 max-w-2xl">
                GABRYX est fait pour évoluer avec ton stack produit. Ajoute une nouvelle app dans ton portefeuille, ton équipe IA se réorganise automatiquement pour la piloter aussi.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="relative z-10 px-6 md:px-12 py-24 md:py-32 text-center">
        <h3 className="text-4xl md:text-6xl font-light tracking-tighter">
          Prêt à piloter ?
        </h3>
        <Link
          to="/app"
          data-testid="footer-cta"
          className="inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-8 py-4 hover:bg-zinc-200 transition mt-10"
        >
          Ouvrir GABRYX
          <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="text-xs text-zinc-600 mt-8">gabryxai.fr · un projet solo · pas de compte, pas de suivi</p>
      </section>

      {/* Idle animation keyframes (scoped) */}
      <style>{`
        @keyframes agentBreathe {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-4px) scale(1.02); }
        }
        @keyframes agentGlow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.9; }
        }
        @keyframes agentFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-6px) rotate(0.5deg); }
          66% { transform: translateY(-3px) rotate(-0.5deg); }
        }
        .agent-idle { animation: agentFloat 4s ease-in-out infinite; }
        .agent-glow { animation: agentGlow 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
}


function AgentCard({ agent, delay }) {
  const Icon = agent.icon;
  return (
    <Link
      to="/app/agents"
      data-testid={`agent-card-${agent.code}`}
      className="group relative border border-white/5 bg-white/[0.02] overflow-hidden transition-all duration-500 hover:border-white/20"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Portrait poster — Delos-inspired magazine cover style */}
      <div className="relative aspect-[3/4] overflow-hidden bg-zinc-900">
        <img
          src={agent.image}
          alt={agent.name}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105 agent-idle"
          style={{ animationDelay: `${delay}ms` }}
          loading="lazy"
        />
        {/* Subtle vignette for text legibility (no color tint — the poster keeps its own hue) */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
        {/* Accent bar at bottom */}
        <div
          className="absolute inset-x-0 bottom-0 h-[3px] agent-glow"
          style={{ backgroundColor: agent.accent, animationDelay: `${delay}ms` }}
        />
        {/* Role icon badge — top right */}
        <div
          className="absolute top-3 right-3 w-9 h-9 grid place-items-center border backdrop-blur-md bg-black/40 transition-all group-hover:scale-110"
          style={{ borderColor: `${agent.accent}80` }}
        >
          <Icon className="w-4 h-4" style={{ color: agent.accent }} />
        </div>
        {/* Editorial text overlay — bottom */}
        <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
          <div className="text-[10px] uppercase tracking-[0.25em] font-mono mb-1" style={{ color: agent.accent }}>
            {agent.role}
          </div>
          <div className="text-xl md:text-2xl font-semibold tracking-tight text-white leading-none">
            {agent.name}
          </div>
          <div className="text-[11px] text-zinc-300 mt-1.5 line-clamp-1 opacity-90">{agent.tagline}</div>
        </div>
      </div>
    </Link>
  );
}


// ---------------------------------------------------------------------------
// Live activity showcase — Delos-inspired "en action" section
// ---------------------------------------------------------------------------

// Icon lookup for the live activity feed (agent icons keyed by string name)
const ACTIVITY_ICONS = {
  Wallet, Megaphone, Scale, Headphones, BarChart3, Radio, Cpu, Sparkles, Rocket, Package,
};

const FALLBACK_ACTIVITIES = [
  {
    agent: "GABY", color: "#3B82F6", image: "/agents/gaby-poster.png", icon_key: "Sparkles",
    goal: "Livrer un briefing chaque matin à 07h00",
    action: "Briefing quotidien livré — 2 pilotes actifs, MRR stable",
    result: { label: "Livré", value: "07:00" }, stamp: "il y a 3 h",
  },
  {
    agent: "CLARA", color: "#EC4899", image: "/agents/clara-poster.png", icon_key: "Megaphone",
    goal: "Générer 3 posts LinkedIn cette semaine",
    action: "Post publié : « Pourquoi j'ai construit GABRYX »",
    result: { label: "Impressions", value: "47" }, stamp: "il y a 8 min",
  },
  {
    agent: "CASPER", color: "#EF4444", image: "/agents/casper-poster.png", icon_key: "Scale",
    goal: "Sécuriser les nouveaux abonnés (CGV + RGPD)",
    action: "CGV v1.2 rédigées — bloc signature prêt à envoyer",
    result: { label: "Document", value: "CGV.pdf" }, stamp: "il y a 15 min",
  },
  {
    agent: "RHÉA", color: "#8B5CF6", image: "/agents/rhea-poster.png", icon_key: "Headphones",
    goal: "Onboarder chaque nouveau pilote à J+3",
    action: "Email J+3 envoyé à 2 pilotes",
    result: { label: "Ouverture", value: "100 %" }, stamp: "il y a 34 min",
  },
];

function LiveActivityShowcase() {
  const [activities, setActivities] = useState(FALLBACK_ACTIVITIES);
  const [source, setSource] = useState("demo");

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/live-activity`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d?.activities) && d.activities.length > 0) {
          setActivities(d.activities);
          setSource(d.source || "live");
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  return (
    <section
      className="relative z-10 px-6 md:px-12 py-16 md:py-24 max-w-7xl mx-auto"
      data-testid="live-activity-section"
    >
      <div className="mb-10 md:mb-14 flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-3 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            {source === "live" ? "En ce moment · flux réel" : "En ce moment"}
          </div>
          <h2 className="text-3xl md:text-5xl font-light tracking-tighter max-w-3xl leading-tight">
            Ils bossent.<br />
            <span className="text-zinc-500">Toi, tu regardes les résultats tomber.</span>
          </h2>
        </div>
        <p className="text-sm text-zinc-500 max-w-xs">
          Chaque objectif que tu confies devient une exécution continue. Aucun prompt à réécrire.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {activities.slice(0, 6).map((a, i) => (
          <ActivityCard key={`${a.agent}-${i}`} activity={a} delay={i * 100} />
        ))}
      </div>
    </section>
  );
}

function ActivityCard({ activity, delay }) {
  const { agent, color, image, goal, action, stamp, icon_key, result } = activity;
  const Icon = ACTIVITY_ICONS[icon_key] || Sparkles;
  return (
    <div
      className="group border border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04] transition-all duration-300 overflow-hidden"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`activity-card-${agent.toLowerCase()}`}
    >
      <div className="flex">
        {/* Poster thumbnail — left column */}
        <div className="relative w-24 md:w-32 flex-shrink-0 overflow-hidden">
          <img src={image} alt={agent} className="w-full h-full object-cover object-top" loading="lazy" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, transparent, ${color}20)` }} />
          <div className="absolute inset-y-0 right-0 w-[2px]" style={{ backgroundColor: color }} />
        </div>

        {/* Activity content — right column */}
        <div className="flex-1 p-4 md:p-5 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <div
              className="text-[10px] font-mono uppercase tracking-[0.25em] font-semibold"
              style={{ color }}
            >
              {agent}
            </div>
            <Icon className="w-3 h-3" style={{ color }} />
            <div className="text-[10px] text-zinc-600 ml-auto">{stamp}</div>
          </div>

          <div className="mb-3">
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 mb-1">Objectif confié</div>
            <div className="text-sm text-zinc-300 italic leading-snug">« {goal} »</div>
          </div>

          <div className="border-l-2 pl-3 mb-3" style={{ borderColor: `${color}80` }}>
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 mb-1">Action</div>
            <div className="text-sm text-white leading-snug flex items-start gap-2">
              <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color }} />
              <span>{action}</span>
            </div>
          </div>

          {result && (
            <div className="inline-flex items-center gap-2 border px-2.5 py-1" style={{ borderColor: `${color}40` }}>
              <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">{result.label}</div>
              <div className="text-xs font-mono font-semibold" style={{ color }}>{result.value}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
